import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../services/apiConfig';
import './FrameworkDefinition.css';

// Use centralized API config for environment-aware URL resolution
const API_BASE_URL = getApiBaseUrl();

interface Framework {
  id: string;
  name: string;
  code: string;
  description: string;
  type: 'Business' | 'Utility' | 'Infrastructure' | 'OT' | 'Security';
  status: 'Active' | 'Inactive';
  default_for_application_type: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  dimension_count: number;
  component_count: number;
  attribute_count: number;
  apr_model_count: number;
  application_count: number;
}

interface FormData {
  name: string;
  code: string;
  description: string;
  type: 'Business' | 'Utility' | 'Infrastructure' | 'OT' | 'Security';
  status: 'Active' | 'Inactive';
  default_for_application_type: string;
}

const FrameworkDefinition: React.FC = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    description: '',
    type: 'Business',
    status: 'Active',
    default_for_application_type: ''
  });

  const [cloneData, setCloneData] = useState({
    name: '',
    code: '',
    description: ''
  });

  useEffect(() => {
    loadFrameworks();
  }, []);

  const loadFrameworks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/frameworks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworks(response.data);

      // Auto-select first framework if none selected
      if (!selectedFramework && response.data.length > 0) {
        setSelectedFramework(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading frameworks:', error);
      setFrameworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFramework = (framework: Framework) => {
    setSelectedFramework(framework);
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setIsEditing(false);
    setSelectedFramework(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      type: 'Business',
      status: 'Active',
      default_for_application_type: ''
    });
  };

  const handleEdit = () => {
    if (!selectedFramework) return;
    setIsEditing(true);
    setIsCreating(false);
    setFormData({
      name: selectedFramework.name,
      code: selectedFramework.code,
      description: selectedFramework.description,
      type: selectedFramework.type,
      status: selectedFramework.status,
      default_for_application_type: selectedFramework.default_for_application_type || ''
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    if (frameworks.length > 0 && !selectedFramework) {
      setSelectedFramework(frameworks[0]);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken');

      if (isCreating) {
        // Create new framework
        const response = await axios.post(`${API_BASE_URL}/frameworks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await loadFrameworks();
        setSelectedFramework(response.data);
        setIsCreating(false);
        alert('Framework created successfully');
      } else if (isEditing && selectedFramework) {
        // Update existing framework
        await axios.put(`${API_BASE_URL}/frameworks/${selectedFramework.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await loadFrameworks();
        setIsEditing(false);
        alert('Framework updated successfully');
      }
    } catch (error: any) {
      console.error('Error saving framework:', error);
      alert(error.response?.data?.error || 'Failed to save framework');
    }
  };

  const handleDelete = async () => {
    if (!selectedFramework) return;

    if (!window.confirm(`Are you sure you want to delete "${selectedFramework.name}"?`)) {
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API_BASE_URL}/frameworks/${selectedFramework.id}`, { headers });
      await loadFrameworks();
      setSelectedFramework(null);
      alert('Framework deleted successfully');
    } catch (error: any) {
      console.error('Error deleting framework:', error);
      const responseData = error.response?.data;

      if (responseData?.canForceDelete) {
        // Models assigned but no scored applications — allow force delete with confirmation
        if (window.confirm(`${responseData.error}\n\nDo you want to proceed with deletion?`)) {
          try {
            await axios.delete(`${API_BASE_URL}/frameworks/${selectedFramework.id}?force=true`, { headers });
            await loadFrameworks();
            setSelectedFramework(null);
            alert('Framework deleted successfully');
          } catch (forceError: any) {
            alert(forceError.response?.data?.error || 'Failed to delete framework');
          }
        }
      } else {
        alert(responseData?.error || 'Failed to delete framework');
      }
    }
  };

  const handleClone = () => {
    if (!selectedFramework) return;
    setShowCloneModal(true);
    setCloneData({
      name: `${selectedFramework.name} (Copy)`,
      code: `${selectedFramework.code}_COPY`,
      description: selectedFramework.description
    });
  };

  const handleCloneSubmit = async () => {
    if (!selectedFramework) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_BASE_URL}/frameworks/${selectedFramework.id}/clone`,
        cloneData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadFrameworks();
      setSelectedFramework(response.data);
      setShowCloneModal(false);
      alert('Framework cloned successfully');
    } catch (error: any) {
      console.error('Error cloning framework:', error);
      alert(error.response?.data?.error || 'Failed to clone framework');
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      Business: '#1976d2',
      Utility: '#388e3c',
      Infrastructure: '#f57c00',
      OT: '#7b1fa2',
      Security: '#d32f2f'
    };
    return colors[type] || '#666';
  };

  return (
    <div className="framework-definition-container">
      <div className="framework-definition-header">
        <h1>Framework Definition</h1>
        <p>Manage APR framework metadata and configurations</p>
      </div>

      <div className="framework-definition-content">
        {/* Left Pane - Framework List */}
        <div className="framework-list-pane">
          <div className="framework-list-header">
            <h2>Frameworks</h2>
            <button
              className="btn-create-framework"
              onClick={handleCreateNew}
            >
              + Create New
            </button>
          </div>

          {loading ? (
            <div className="loading-message">Loading frameworks...</div>
          ) : frameworks.length === 0 ? (
            <div className="empty-message">No frameworks found</div>
          ) : (
            <div className="framework-list">
              {frameworks.map((framework) => (
                <div
                  key={framework.id}
                  className={`framework-list-item ${selectedFramework?.id === framework.id ? 'selected' : ''}`}
                  onClick={() => handleSelectFramework(framework)}
                >
                  <div className="framework-item-header">
                    <h3>{framework.name}</h3>
                    <span
                      className="framework-type-badge"
                      style={{ backgroundColor: getTypeColor(framework.type) }}
                    >
                      {framework.type}
                    </span>
                  </div>
                  <div className="framework-item-code">{framework.code}</div>
                  <div className="framework-item-stats">
                    <span>{framework.dimension_count}D</span>
                    <span>{framework.component_count}C</span>
                    <span>{framework.attribute_count}A</span>
                    <span className="app-count">
                      {framework.application_count} {framework.application_count === 1 ? 'app' : 'apps'}
                    </span>
                  </div>
                  <div className="framework-item-status">
                    <span className={`status-badge ${framework.status.toLowerCase()}`}>
                      {framework.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Pane - Framework Details */}
        <div className="framework-details-pane">
          {isCreating || isEditing ? (
            <div className="framework-form">
              <div className="form-header">
                <h2>{isCreating ? 'Create New Framework' : 'Edit Framework'}</h2>
              </div>

              <div className="form-content">
                <div className="form-group">
                  <label>Framework Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter framework name"
                  />
                </div>

                <div className="form-group">
                  <label>Framework Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="Enter unique code (e.g., APR_BUSINESS)"
                    disabled={isEditing} // Code cannot be changed after creation
                  />
                  {isEditing && (
                    <small className="field-note">Code cannot be changed after creation</small>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    >
                      <option value="Business">Business</option>
                      <option value="Utility">Utility</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="OT">OT (Operational Technology)</option>
                      <option value="Security">Security</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Default for Application Type</label>
                  <input
                    type="text"
                    value={formData.default_for_application_type}
                    onChange={(e) => setFormData({ ...formData, default_for_application_type: e.target.value })}
                    placeholder="e.g., Business, Utility (optional)"
                  />
                  <small className="field-note">
                    If specified, this framework will be automatically assigned to applications of this type
                  </small>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder="Describe the purpose and scope of this framework"
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    className="btn-save"
                    onClick={handleSave}
                    disabled={!formData.name || !formData.code}
                  >
                    {isCreating ? 'Create Framework' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : selectedFramework ? (
            <div className="framework-view">
              <div className="view-header">
                <div>
                  <h2>{selectedFramework.name}</h2>
                  <div className="view-subtitle">
                    <span
                      className="framework-type-badge"
                      style={{ backgroundColor: getTypeColor(selectedFramework.type) }}
                    >
                      {selectedFramework.type}
                    </span>
                    <span className={`status-badge ${selectedFramework.status.toLowerCase()}`}>
                      {selectedFramework.status}
                    </span>
                  </div>
                </div>
                <div className="view-actions">
                  <button className="btn-action" onClick={handleEdit}>
                    Edit
                  </button>
                  <button className="btn-action" onClick={handleClone}>
                    Clone
                  </button>
                  <button
                    className="btn-action btn-delete"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="view-content">
                <div className="info-section">
                  <h3>Framework Information</h3>
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <td className="info-table-label">Code</td>
                        <td className="info-table-value">{selectedFramework.code || '—'}</td>
                        <td className="info-table-label">Type</td>
                        <td className="info-table-value">{selectedFramework.type || '—'}</td>
                      </tr>
                      <tr>
                        <td className="info-table-label">Status</td>
                        <td className="info-table-value">{selectedFramework.status || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="info-section">
                  <h3>Framework Structure</h3>
                  <div className="structure-stats structure-stats-compact">
                    <div className="stat-card stat-card-compact">
                      <div className="stat-value stat-value-compact">{selectedFramework.dimension_count || 0}</div>
                      <div className="stat-label">Dimensions</div>
                    </div>
                    <div className="stat-card stat-card-compact">
                      <div className="stat-value stat-value-compact">{selectedFramework.component_count || 0}</div>
                      <div className="stat-label">Components</div>
                    </div>
                    <div className="stat-card stat-card-compact">
                      <div className="stat-value stat-value-compact">{selectedFramework.attribute_count || 0}</div>
                      <div className="stat-label">Attributes</div>
                    </div>
                    <div className="stat-card stat-card-compact">
                      <div className="stat-value stat-value-compact">{selectedFramework.apr_model_count || 0}</div>
                      <div className="stat-label">APR Models</div>
                    </div>
                    <div className="stat-card stat-card-compact">
                      <div className="stat-value stat-value-compact">{selectedFramework.application_count || 0}</div>
                      <div className="stat-label">Applications</div>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <h3>Description</h3>
                  <p className="description-text">
                    {selectedFramework.description || 'No description provided'}
                  </p>
                </div>

                <div className="info-section">
                  <h3>Metadata</h3>
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <td className="info-table-label">Created</td>
                        <td className="info-table-value">{new Date(selectedFramework.created_at).toLocaleString()}</td>
                        <td className="info-table-label">Created By</td>
                        <td className="info-table-value">{selectedFramework.created_by || '—'}</td>
                      </tr>
                      <tr>
                        <td className="info-table-label">Last Updated</td>
                        <td className="info-table-value">{new Date(selectedFramework.updated_at).toLocaleString()}</td>
                        <td className="info-table-label">Updated By</td>
                        <td className="info-table-value">{selectedFramework.updated_by || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-selection">
              <p>Select a framework from the list or create a new one</p>
            </div>
          )}
        </div>
      </div>

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="modal-overlay" onClick={() => setShowCloneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Clone Framework</h2>
              <button className="modal-close" onClick={() => setShowCloneModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Create a copy of "{selectedFramework?.name}" with all its structure and mappings</p>

              <div className="form-group">
                <label>New Framework Name *</label>
                <input
                  type="text"
                  value={cloneData.name}
                  onChange={(e) => setCloneData({ ...cloneData, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>New Framework Code *</label>
                <input
                  type="text"
                  value={cloneData.code}
                  onChange={(e) => setCloneData({ ...cloneData, code: e.target.value.toUpperCase() })}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={cloneData.description}
                  onChange={(e) => setCloneData({ ...cloneData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCloneModal(false)}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleCloneSubmit}
                disabled={!cloneData.name || !cloneData.code}
              >
                Clone Framework
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameworkDefinition;
